import { HttpErrorResponse } from '@angular/common/http';
/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  getDeepFromObject,
  NB_AUTH_OPTIONS,
  NbAuthResult,
  NbAuthService,
  NbAuthSocialLink,
} from '@nebular/auth';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { fadeInRightOnEnterAnimation } from 'angular-animations';
import { Subject } from 'rxjs';
import { RegistrationCompleteDialogComponent } from '../../../components/dialogs/registration-complete-dialog/registration-complete-dialog.component';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'nb-register',
  styleUrls: ['./register.component.scss'],
  templateUrl: './register.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInRightOnEnterAnimation()],
})
export class NbRegisterComponent {
  redirectDelay: number = 0;
  showMessages: any = {};
  strategy: string = '';

  submitted = false;
  errors: string[] = [];
  messages: string[] = [];

  /** Set when the address belongs to an account that was never confirmed. */
  unconfirmedEmail: string | null = null;
  resendLoading = false;
  resendSuccess = false;
  resendError = '';
  user: any = {
    newsletter: false,
  };
  socialLinks: NbAuthSocialLink[] = [];

  captureToken: string | null = null;

  renewToken$: Subject<void> = new Subject<void>();

  constructor(
    protected service: NbAuthService,
    @Inject(NB_AUTH_OPTIONS) protected options = {},
    protected cd: ChangeDetectorRef,
    protected nbToastrService: NbToastrService,
    protected router: Router,
    protected nbDialogService: NbDialogService,
    private authService: AuthService
  ) {
    this.redirectDelay = this.getConfigValue('forms.register.redirectDelay');
    this.showMessages = this.getConfigValue('forms.register.showMessages');
    this.strategy = this.getConfigValue('forms.register.strategy');
    this.socialLinks = this.getConfigValue('forms.login.socialLinks');
  }

  onCaptureTokenReceived(token: string | null): void {
    this.captureToken = token;
  }

  register(): void {
    this.errors = this.messages = [];
    this.unconfirmedEmail = null;
    this.resendSuccess = false;
    this.resendError = '';
    this.submitted = true;

    this.user.email = this.user.email.replace(/\s/g, '').toLowerCase();
    this.user.cf_turnstile_response = this.captureToken;

    this.captureToken = null;
    this.renewToken$.next();

    this.service
      .register(this.strategy, this.user)
      .subscribe((result: NbAuthResult) => {
        this.submitted = false;
        if (result.isSuccess()) {
          this.messages = result.getMessages();

          // The account is created even when the welcome mail could not be
          // delivered. The backend reports which of the two happened, so the
          // dialog can offer a resend instead of "check your inbox".
          const body: any = result.getResponse()?.body;
          const emailSent = body?.data?.welcome_email_sent !== false;

          this.nbDialogService.open(RegistrationCompleteDialogComponent, {
            context: {
              email: this.user.email,
              emailSent,
            },
          });
        } else if (this.isUnconfirmed(result)) {
          // Not a duplicate to turn away: the account exists but was never
          // confirmed, and registering again is precisely what someone does
          // after missing the original link. Offer the confirmation mail
          // instead of "Email already registered", which is a dead end —
          // the same treatment login and password-reset already give.
          this.renewToken$?.next();
          this.unconfirmedEmail = this.user.email ?? null;
          this.errors = [];
        } else {
          this.renewToken$?.next();
          this.errors = result.getErrors();
        }

        this.cd.detectChanges();
      });
  }

  /** True when registration was refused because the account is unconfirmed. */
  private isUnconfirmed(result: NbAuthResult): boolean {
    const response = result.getResponse() as any;
    return response?.headers?.get?.('X-Error-Code') === 'account_unconfirmed';
  }

  resendConfirmation(): void {
    if (!this.unconfirmedEmail || this.resendLoading) {
      return;
    }
    this.resendLoading = true;
    this.resendError = '';

    this.authService.resendConfirmation(this.unconfirmedEmail).subscribe({
      next: () => {
        this.resendLoading = false;
        this.resendSuccess = true;
        this.cd.detectChanges();
      },
      error: (err: HttpErrorResponse) => {
        this.resendLoading = false;
        // 429 is the resend cooldown, not a failure. Saying "could not
        // be sent" would tell someone to give up at the exact moment
        // they only have to wait a minute.
        this.resendError =
          err?.status === 429
            ? 'pages.register.unconfirmed.resendCooldown'
            : 'pages.register.unconfirmed.resendError';
        this.cd.detectChanges();
      },
    });
  }

  getConfigValue(key: string): any {
    return getDeepFromObject(this.options, key, null);
  }
}
